<template>
    <v-dialog v-model="dialog" width="1300" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div class="w-75 text-truncate text-white">
                    <span class="font-weight-bold text-h6 text-white">Add Admin Load from Previous Entries</span>
                    <div class="text-caption text-white">Select one or more previous admin load entries to add to the current semester.</div>
                </div>
                <v-btn v-if="!loading" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 180px);">
                <v-alert type="info" variant="tonal" class="mb-4">
                    Previous entries are copied with their stored file reference, so the same file can be reused without uploading it again. You can still upload new files later if needed.
                </v-alert>

                <v-data-table
                    v-model="selectedItems"
                    :headers="headers"
                    :items="previousAdminLoads"
                    :loading="loading"
                    item-value="id"
                    show-select
                    hide-default-footer
                    class="elevation-1"
                >
                    <template v-slot:loading>
                        <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                    </template>
                    <template v-slot:item.so_number="{ item }">
                        <span>{{ item.so_number }}</span>
                    </template>
                    <template v-slot:item.description="{ item }">
                        <div class="text-truncate" style="max-width: 500px;">{{ item.description }}</div>
                    </template>
                    <template v-slot:item.hours="{ item }">
                        <span>{{ item.hours }}</span>
                    </template>
                    <template v-slot:item.unit="{ item }">
                        <span>{{ item.unit }}</span>
                    </template> 
                    <template v-slot:item.files="{ item }">
                        <a
                            v-if="item.files"
                            href="#"
                            class="text-primary text-decoration-underline"
                            @click.prevent="showFile(item.files)"
                        >
                            {{ item.files }}
                        </a>
                        <span v-else>No Files Attached</span>
                    </template>
                    <template v-slot:item.to_be_counted="{ item }">
                        <v-icon v-if="item.to_be_counted" color="green">mdi-check</v-icon>
                        <v-icon v-else color="red">mdi-close</v-icon>
                    </template>
                    <template v-slot:no-data>
                        <span>No previous admin load entries found.</span>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3">
                <v-spacer></v-spacer>
                <v-btn variant="outlined" @click="closeDialog()" :disabled="loading">Cancel</v-btn>
                <v-btn color="darkGreen" variant="elevated" :loading="loading" @click="saveSelected()">
                    Add Selected
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <ViewFileDialog ref="ViewFileDialog" />
</template>

<script>
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'
import ViewFileDialog from '@/components/Dialogs/Sub-Dialogs/ViewFileDialog.vue'

export default defineComponent({
    emits: ['refresh'],
    components: {
        ViewFileDialog,
    },
    data() {
        return {
            dialog: false,
            loading: false,
            user_id: null,
            semester_schoolyear: null,
            previousAdminLoads: [],
            selectedItems: [],
            headers: [
                { title: 'SO Number', key: 'so_number', sortable: true, align: 'start' },
                { title: 'Description', key: 'description', sortable: true, align: 'start' },
                { title: 'Hours', key: 'hours', sortable: true, align: 'center' },
                { title: 'Unit', key: 'unit', sortable: true, align: 'center' },
                { title: 'Files Attached', key: 'files', sortable: true, align: 'center' },
                { title: 'To Be Counted', key: 'to_be_counted', sortable: true, align: 'center' },
            ],
        }
    },
    methods: {
        async initAdd(userId, semesterSchoolyearId) {
            this.dialog = true
            this.user_id = userId
            this.semester_schoolyear = semesterSchoolyearId || this.$store.state.semester_schoolyear.id
            this.selectedItems = []
            this.previousAdminLoads = []
            await this.loadPreviousAdminLoads()
        },
        async loadPreviousAdminLoads() {
            if (!this.user_id) return
            this.loading = true
            const res = await this.axiosCall(`/admin-load/findPreviousByUserAndSemester/${this.user_id}/${this.semester_schoolyear}`, 'get', null, true)
            this.previousAdminLoads = Array.isArray(res?.data) ? res.data : []
            this.loading = false
        },
        async saveSelected() {
            const { showSnackbar } = useSnackbar()
            if (!this.selectedItems || this.selectedItems.length === 0) {
                showSnackbar('Please select at least one previous admin load entry.', 'warning')
                return
            }

            this.loading = true
            const payload = {
                user_id: this.user_id,
                semester_schoolyear: this.semester_schoolyear,
                admin_load_ids: this.selectedItems,
            }

            const res = await this.axiosCall('/admin-load/createFromPrevious', 'post', payload, true)
            if (res?.data?.status === 200) {
                this.loading = false
                this.$emit('refresh')
                showSnackbar('Selected admin load entries were added to the current semester.')
                this.closeDialog()
            } else {
                this.loading = false
                showSnackbar('Unable to add selected admin load entries.', 'error')
            }
        },
        showFile(file) {
            this.$refs.ViewFileDialog.viewFile(file)
        },
        closeDialog() {
            this.selectedItems = []
            this.previousAdminLoads = []
            this.dialog = false
        },
    },
})
</script>
